import { EXPECTED_TENANT_HEADER, EXPECTED_USER_HEADER, type RequestIdentity } from '@epc/contracts';
import { isSessionLost, wipeOfflineDataSafe } from './offline-wipe';
import { reportServerTrouble } from './updates';

/** Ошибка API с кодом и сообщением на русском — показывается пользователю как есть. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Array<{ path: string; message: string }> | unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Ошибки по полям формы: { email: 'Неверный адрес почты' } */
  fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    const out: Record<string, string> = {};
    for (const d of this.details as Array<{ path?: string; message?: string }>) {
      if (d.path && d.message && !out[d.path]) out[d.path] = d.message;
    }
    return out;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  form?: FormData;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Ключ сохраняется отправителем вместе с телом команды и не меняется при повторе. */
  idempotencyKey?: string;
  /** Владелец локальной команды; не подставлять текущего пользователя вместо исходного. */
  expectedIdentity?: RequestIdentity;
}

const BASE = '/api/v1';

/** Без связи и при недоступном сервере (502–504) — понятная причина и что делать; данные формы не трогаем */
const UNREACHABLE =
  'Нет связи с порталом: идёт обновление или пропал интернет. Повторите через минуту — введённое на странице не пропадёт.';

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-epc-csrf': '1', accept: 'application/json' };
  if (opts.idempotencyKey !== undefined) headers['idempotency-key'] = opts.idempotencyKey;
  if (opts.expectedIdentity) {
    headers[EXPECTED_USER_HEADER] = opts.expectedIdentity.userId;
    headers[EXPECTED_TENANT_HEADER] = opts.expectedIdentity.tenantId;
  }
  let body: BodyInit | undefined;
  if (opts.form) {
    body = opts.form;
  } else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  let url = `${BASE}${path}`;
  if (opts.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query))
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    const s = qs.toString();
    if (s) url += `?${s}`;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    reportServerTrouble();
    throw new ApiError(0, 'network', UNREACHABLE);
  }

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // Сеанс кончился: запомненное для работы без связи на этом телефоне больше не принадлежит вошедшему
    if (isSessionLost(path, res.status)) void wipeOfflineDataSafe();
    const e = (data as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    // 5xx без ответа API (502 от Caddy, 500 прокси) — сервер перезапускается при выкладке или недоступен
    if (res.status >= 500 && !e?.code) {
      reportServerTrouble();
      throw new ApiError(res.status, 'unavailable', UNREACHABLE);
    }
    throw new ApiError(
      res.status,
      e?.code ?? `http_${res.status}`,
      e?.message ?? `Ошибка ${res.status}`,
      e?.details,
    );
  }
  return data as T;
}
